/**
 * C2 CitationRevocationChip — React component (Bundle 1 Recovery-A).
 *
 * Renders the chip state derived from {@link deriveChipState}. PD-27 copy +
 * accessibility (role + aria-label). NO network calls, NO router coupling,
 * NO routing into broad AppShell — caller supplies the `CitationView` + `now`
 * + optional click handler for the action.
 *
 * For an `active` citation (kind=none), the component renders nothing — the
 * citing surface is responsible for the active-state visual.
 */
import { useState } from 'react';
import { deriveChipState } from './model';
import type { CitationView } from './types';

export interface CitationRevocationChipProps {
  citation: CitationView;
  /** Injected for deterministic tests; default `Date.now`. */
  now?: () => number;
  /** Caller wires this to the actual fetch POST + audit emit; component only
   *  surfaces the endpoint and intent. `artifact_id` is included so the
   *  caller can invalidate the by-artifact citation query (Bundle 1 C2
   *  data feed) on success without a separate lookup. */
  onAction?: (intent: { citation_id: string; artifact_id: string; action: 'acknowledge' | 'escalate'; endpoint: string }) => void;
  /** Default false: chip starts collapsed; click expands reason + action. */
  defaultExpanded?: boolean;
  /**
   * Bundle 1 C2 action-error-UX wave: when true, the action button is
   * disabled + `aria-busy="true"` and shows an "in progress" suffix.
   * Resolved by the caller via the mutation hook's `isPendingFor(citation_id)`.
   * Default false.
   */
  isPending?: boolean;
  /**
   * Bundle 1 C2 action-error-UX wave: when non-null, an inline
   * `role="alert"` element renders below the action button with a
   * user-safe retry message. Clicking the action button again is the
   * retry path — the mutation's `onMutate` clears the error automatically.
   * Default null. The Error.message is NOT shown to users (it may contain
   * backend internals); a generic action-specific copy is used instead.
   */
  error?: Error | null;
  /**
   * Plan 32 Option A — Bundle 1 C2 optimistic-UX wave. When non-null AND
   * `isPending=true`, the action button's label is replaced with an
   * action-specific transient status ("Acknowledging…" / "Contacting
   * owner…") instead of the default `"<label> (in progress…)"` suffix.
   * Cleared by the caller on mutation settled. Honest semantics: the chip
   * does NOT mark the citation as ack'd/escalated server-side; the real
   * `ack_status` arrives via the by-artifact refetch on success. Wire to
   * `useCitationRevocationActions().optimisticActionFor(citation_id)`.
   * Default null.
   */
  optimisticAction?: 'acknowledge' | 'escalate' | null;
  /**
   * Plan 32 Option B — Bundle 1 C2 dismiss-button wave. When supplied AND
   * `error` is non-null, an accessible Dismiss control renders inside the
   * inline `role="alert"`. Clicking it invokes the callback with the
   * citation_id; the caller is expected to wire it to
   * `useCitationRevocationActions().clearError(citation_id)`. The next
   * mutation lifecycle (onMutate / onError) still clears/sets errors
   * normally, so dismiss does NOT permanently hide future errors. The
   * retry-via-action-button path remains unchanged. Default undefined →
   * no dismiss control renders (existing alert-only behavior preserved).
   */
  onDismissError?: (citationId: string) => void;
}

export function CitationRevocationChip(props: CitationRevocationChipProps): React.ReactElement | null {
  const now = (props.now ?? Date.now)();
  const state = deriveChipState(props.citation, now);
  const [expanded, setExpanded] = useState<boolean>(props.defaultExpanded ?? false);

  if (state.kind === 'none') return null;

  // Bundle 1 C2 action-error-UX wave — local pending + error feedback.
  // Pending disables the action button (no duplicate POST). Error renders
  // an inline `role="alert"` below the action with a user-safe retry copy
  // and is associated to the button via aria-describedby for screen readers.
  // Error.message itself is NOT shown to users (may carry backend internals).
  const isPending = props.isPending ?? false;
  const error = props.error ?? null;
  const optimisticAction = props.optimisticAction ?? null;
  const errorMessageId = `citation-chip-action-error-${props.citation.citation_id}`;
  const errorCopy =
    state.action === 'acknowledge'
      ? "Couldn't acknowledge — please try again."
      : state.action === 'escalate'
        ? "Couldn't contact owner — please try again."
        : "Couldn't complete action — please try again.";
  // Plan 32 Option A — optimistic-UX label. When pending AND the caller has
  // supplied which specific action is in flight, swap the button label for
  // an action-specific transient status. Honest: only fires while the POST
  // is in flight (cleared by the hook on settled), and is purely a label
  // change — does NOT alter `data-action` / `data-endpoint` / the chip's
  // derived kind, which continue to reflect the real `ack_status` from the
  // backend until the by-artifact refetch lands.
  const dismissAriaLabel =
    state.action === 'acknowledge'
      ? 'Dismiss acknowledge error'
      : state.action === 'escalate'
        ? 'Dismiss contact owner error'
        : 'Dismiss citation action error';

  return (
    <span
      role="status"
      data-testid="citation-revocation-chip"
      data-kind={state.kind}
      data-citation-id={props.citation.citation_id}
      data-artifact-id={props.citation.artifact_id}
      aria-label={state.aria_label}
      className={`citation-chip citation-chip-${state.kind}`}
    >
      <button
        type="button"
        data-testid="citation-chip-label"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        className="citation-chip-label"
      >
        {state.label}
      </button>
      {expanded && (
        <span className="citation-chip-detail" data-testid="citation-chip-detail">
          {state.reason !== undefined && (
            <span className="citation-chip-reason" data-testid="citation-chip-reason">
              {state.reason}
            </span>
          )}
          {state.action !== null && state.action_endpoint !== undefined && (() => {
            // Capture narrowed values in consts so the onClick closures don't
            // need non-null assertions (typescript.md: NEVER use `!`).
            // Wave 092 peer-review LOW finding — replaces the prior
            // `state.action!` / `state.action_endpoint!` / `props.onDismissError!`
            // assertions with const-narrowing after the explicit guard.
            const action = state.action;
            const actionEndpoint = state.action_endpoint;
            const onDismissError = props.onDismissError;
            return (
            <>
              <button
                type="button"
                data-testid="citation-chip-action"
                data-action={action}
                data-endpoint={actionEndpoint}
                disabled={isPending}
                aria-busy={isPending}
                aria-describedby={error !== null ? errorMessageId : undefined}
                onClick={() => {
                  // Pending guard: defensive — `disabled` already blocks the
                  // browser-default click, but a programmatic `.click()` or
                  // a stale event handler could still fire. Skip the POST.
                  if (isPending) return;
                  if (props.onAction === undefined) return;
                  props.onAction({
                    citation_id: props.citation.citation_id,
                    artifact_id: props.citation.artifact_id,
                    action,
                    endpoint: actionEndpoint,
                  });
                }}
              >
                {isPending && optimisticAction === 'acknowledge'
                  ? 'Acknowledging…'
                  : isPending && optimisticAction === 'escalate'
                    ? 'Contacting owner…'
                    : `${state.action_label}${isPending ? ' (in progress…)' : ''}`}
              </button>
              {error !== null && (
                <span
                  role="alert"
                  id={errorMessageId}
                  data-testid="citation-chip-action-error"
                  className="citation-chip-action-error"
                >
                  {errorCopy}
                  {onDismissError !== undefined && (
                    <button
                      type="button"
                      data-testid="citation-chip-action-error-dismiss"
                      aria-label={dismissAriaLabel}
                      onClick={() => onDismissError(props.citation.citation_id)}
                      className="citation-chip-action-error-dismiss"
                    >
                      ✕
                    </button>
                  )}
                </span>
              )}
            </>
            );
          })()}
        </span>
      )}
    </span>
  );
}
